import { message, Modal, Slider, Upload, Select } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';
import { createPortfolio } from '../../../api/copyTrading';
import API from '@/utils/api';
import { VITE_APP_API_MEDIA_DEV_URL } from '@/app/constants/APIKEYS';
import { useToken } from '@/app/hooks/auth/token';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';

const { TextArea } = Input;

const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};

const beforeUpload = (file) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message.error('You can only upload JPG/PNG file!');
  }
  const isLt10M = file.size / 1024 / 1024 <= 10;
  if (!isLt10M) {
    message.error('Image must be 10MB or smaller!');
  }
  return isJpgOrPng && isLt10M;
};

export const CopyRegisterModal = ({ setOpen, open }: any) => {
  const { t } = useTranslation();
  const { data: token } = useToken();

  // Form State
  const [imageUrl, setImageUrl] = useState<string>(); // 미리보기용 (Base64)
  const [imageKey, setImageKey] = useState<string>(); // 실제 저장 키
  const [portfolioName, setPortfolioName] = useState('');
  const [description, setDescription] = useState('');
  const [profitShareRate, setProfitShareRate] = useState(10); // 0-30%
  const [minCopyAmount, setMinCopyAmount] = useState(100);

  // 이미지 업로드 상태
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = async (info) => {
    if (loading) return;
    setLoading(true);

    try {
      // 1. Presigned URL 요청
      const response = await API.post('/upload/presigned-url', token, {
        usage: 'copy-portfolio-image',
        fileExtension: info?.file?.type === 'image/jpeg' ? 'jpg' : 'png',
      });

      if (response.success) {
        // 2. R2에 직접 업로드
        const fileUrl = response.result.url;
        const uploadRes = await fetch(fileUrl, {
          method: 'PUT',
          body: info?.file?.originFileObj,
          headers: { 'Content-Type': info?.file?.type },
        });

        if (uploadRes.ok) {
          // 3. 업로드 성공 확인
          const fullImageUrl = `${VITE_APP_API_MEDIA_DEV_URL}/${response.result.key}`;
          const checkRes = await fetch(fullImageUrl);

          if (checkRes.ok) {
            const key = response.result.key;
            console.log('[Image Upload] Success - Key:', key);
            setImageKey(key); // 실제 저장용
            getBase64(info.file.originFileObj, (url) => {
              setImageUrl(url); // 미리보기용
              console.log('[Image Upload] Preview URL set');
            });
            message.success(
              t('imageUploadSuccess') || 'Image uploaded successfully'
            );
          } else {
            console.error('[Image Upload] Check failed for URL:', fullImageUrl);
            message.error(
              t('imageUploadFailed') ||
                'Uploaded image is not yet available. Please try again later.'
            );
          }
        }
      }
    } catch (error) {
      console.error('Upload failed', error);
      message.error(t('imageUploadFailed') || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = () => {
    setImageUrl(undefined);
    setImageKey(undefined);
  };

  const handleCancel = () => {
    setOpen(false);
    // 폼 초기화
    setImageUrl(undefined);
    setImageKey(undefined);
    setPortfolioName('');
    setDescription('');
    setProfitShareRate(10);
    setMinCopyAmount(100);
  };

  const handleOk = async () => {
    // 입력값 검증
    if (!portfolioName.trim()) {
      message.error(
        t('pleaseEnterPortfolioName') || 'Please enter portfolio name'
      );
      return;
    }

    if (portfolioName.length < 8) {
      message.error(t('portfolioNameMinLengthError'));
      return;
    }

    if (!imageKey) {
      message.warning(t('pleaseUploadImage'));
      console.warn('[Portfolio Create] No imageKey - upload required');
      return;
    }

    console.log('[Portfolio Create] Submitting with imageKey:', imageKey);

    setSubmitting(true);

    try {
      // API 호출
      const requestData = {
        portfolioName: portfolioName,
        description: description || undefined,
        imageUrl: imageKey, // R2 key (예: "copy-portfolios/abc123.jpg")
        profitShareRate: profitShareRate,
        minCopyAmount: minCopyAmount,
      };

      console.log('[Portfolio Create] Request data:', requestData);

      const response = await createPortfolio(requestData);

      if (response.success) {
        message.success(
          t('portfolioCreatedSuccessfully') || 'Portfolio created successfully!'
        );
        setOpen(false);
        handleCancel(); // 폼 초기화

        // 페이지 새로고침 또는 Store 갱신 필요
        window.location.reload();
      } else {
        message.error(response.error?.message || 'Failed to create portfolio');
      }
    } catch (error: any) {
      console.error('Create portfolio error:', error);
      message.error(
        error.response?.data?.error?.message || 'Failed to create portfolio'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledModal
      open={open}
      width={424}
      centered={true}
      closable={false}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
    >
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>{t('becomeALeader')}</ModalTitle>
          <CloseButton onClick={handleCancel}>
            <CloseIcon
              src="/assets/newHome/copy-trading/close-icon.svg"
              alt="Close"
            />
          </CloseButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          {/* Portfolio Image */}
          <FormSection>
            <FormLabel>{t('portfolioImage')}</FormLabel>
            <ImageSection>
              <ImageWrapper>
                {imageUrl ? (
                  <PortfolioImage src={imageUrl} alt="Portfolio" />
                ) : (
                  <Upload
                    name="avatar"
                    listType="picture-circle"
                    className="avatar-uploader"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    onChange={handleChange}
                  >
                    <UploadButton>
                      {loading ? <LoadingOutlined /> : <PlusOutlined />}
                    </UploadButton>
                  </Upload>
                )}
              </ImageWrapper>
              {imageUrl && (
                <ImageActions>
                  <UpdateButton
                    onClick={() => {
                      const input = document.querySelector(
                        '.hidden-avatar-uploader input'
                      ) as HTMLInputElement;
                      input?.click();
                    }}
                  >
                    {t('update')}
                  </UpdateButton>
                  <DeleteButton onClick={handleDeleteImage}>
                    {t('delete')}
                  </DeleteButton>
                </ImageActions>
              )}
            </ImageSection>
            {/* 숨겨진 Upload - 수정 버튼용 (항상 마운트) */}
            <HiddenUploadWrapper>
              <Upload
                name="avatar"
                className="hidden-avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleChange}
              >
                <span />
              </Upload>
            </HiddenUploadWrapper>
          </FormSection>

          {/* Portfolio Name */}
          <FormSection>
            <FormLabel>{t('portfolioName')}</FormLabel>
            <PortfolioNameInput
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
            />
            <HelperText>{t('portfolioNameHelper')}</HelperText>
          </FormSection>

          {/* Description */}
          <FormSection>
            <FormLabel>{t('portfolioDescription')}</FormLabel>
            <DescriptionInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('portfolioDescription')}
              rows={3}
            />
          </FormSection>

          {/* Profit Share Rate */}
          <FormSection>
            <FormLabel>{t('profitShareRate')}</FormLabel>
            <SliderContainer>
              <div
                style={{
                  position: 'relative',
                  boxSizing: 'border-box',
                  padding: '12px 17px 0 14px',
                  marginBottom: '8px',
                  width: '100%',
                }}
              >
                <Slider
                  min={0}
                  max={30}
                  value={profitShareRate}
                  onChange={setProfitShareRate}
                  marks={{
                    0: {
                      label: (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#B3B5BD',
                            fontFamily: 'NanumSquareRound',
                            letterSpacing: -0.36,
                            position: 'relative',
                            left: 'calc(50% - 4px)',
                            top: '4px',
                          }}
                        >
                          0%
                        </div>
                      ),
                    },
                    10: {
                      label: <strong style={{ fontSize: 11 }}>{''}</strong>,
                    },
                    20: {
                      label: <strong style={{ fontSize: 11 }}>{''}</strong>,
                    },
                    30: {
                      label: (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#B3B5BD',
                            fontFamily: 'NanumSquareRound',
                            letterSpacing: -0.36,
                            position: 'relative',
                            right: 'calc(50% - 4px)',
                            top: '4px',
                          }}
                        >
                          30%
                        </div>
                      ),
                    },
                  }}
                  styles={{
                    rail: {
                      background: '#2A2C2E',
                      height: 3,
                    },
                    track: {
                      background: '#07D993',
                      height: 3,
                    },
                    handle: {
                      border: '1px solid #07D993',
                      background: '#07D993',
                    },
                  }}
                />
              </div>
              <SliderCurrentValue>{profitShareRate}%</SliderCurrentValue>
            </SliderContainer>
          </FormSection>

          {/* Minimum Copy Amount */}
          <FormSection>
            <FormLabel>{t('minimumCopyAmount')}</FormLabel>
            <AmountSelect
              value={minCopyAmount}
              onChange={(value) => setMinCopyAmount(value as number)}
              suffixIcon={
                <DropdownIcon
                  src="/assets/newHome/copy-trading/dropdown-arrow-down.svg"
                  alt="Dropdown"
                />
              }
            >
              <Select.Option value={100}>100 USDT</Select.Option>
              <Select.Option value={200}>200 USDT</Select.Option>
              <Select.Option value={500}>500 USDT</Select.Option>
              <Select.Option value={1000}>1000 USDT</Select.Option>
              <Select.Option value={5000}>5000 USDT</Select.Option>
            </AmountSelect>
          </FormSection>
        </ModalContent>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={handleCancel}>{t('cancel')}</CancelButton>
          <SubmitButton onClick={handleOk} disabled={submitting}>
            {submitting ? t('registering') : t('leaderRegistration')}
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #0c2323;
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
    max-height: 90vh;
  }

  .ant-modal-body {
    padding: 0;
    max-height: 90vh;
    overflow-y: auto;

    /* 커스텀 스크롤바 */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(7, 217, 147, 0.5);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(7, 217, 147, 0.7);
    }
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: auto;
  height: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px 35px;
`;

const ModalTitle = styled.h2`
  font-family: 'Aggro Medium', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 35px;
  flex: 1;
  overflow-y: visible;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const ImageSection = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const HiddenUploadWrapper = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
`;

const ImageWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 999px;
  background: #ff8a00;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  .avatar-uploader {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadButton = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 24px;
`;

const ImageActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const UpdateButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #07d993;
  border-radius: 8px;
  color: #07d993;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const DeleteButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 8px;
  color: #b3b5bd;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PortfolioNameInput = styled(Input)`
  height: 50px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #07d993;
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;

  &::placeholder {
    color: #ffffff;
  }

  &:focus {
    border-color: #07d993;
  }
`;

const HelperText = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  line-height: 16px;
`;

const DescriptionInput = styled(TextArea)`
  height: 50px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.28px;
  resize: none;

  &::placeholder {
    color: #b3b5bd;
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SliderCurrentValue = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.32px;
  text-align: center;
`;

const AmountSelect = styled(Select)`
  width: 100%;
  height: 50px;
  margin-bottom: 24px;

  .ant-select-selector {
    height: 50px !important;
    padding: 10px 20px !important;
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 12px !important;
    color: #ffffff !important;
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: -0.28px !important;
  }

  .ant-select-selection-item {
    line-height: 30px !important;
    color: #ffffff !important;
  }

  .ant-select-arrow {
    color: #ffffff !important;
  }
`;

const DropdownIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 30px 35px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubmitButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: #07d993;
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
